// @flow
import moment from 'moment/moment'
import type { Answer } from 'types/answer'
import type { ConditionalRule } from 'types/conditional-rule'
import type { ConditionalRuleGroup } from 'types/conditional-rule-group'
import type { Question } from 'types/question'

const MAX_DEPTH = 10

class ConditionalRulesResolver {
  answers: Array<Answer> = []
  questions: Array<Question> = []
  depth: number = 0

  constructor(answers: ?Array<Answer>, questions: Array<Question>) {
    this.answers = answers || []
    this.questions = questions || []
  }

  equals(answer_raw: string, expected_answer: string): boolean {
    return answer_raw === expected_answer
  }

  notEquals(answer_raw: string, expected_answer: string): boolean {
    return !this.equals(answer_raw, expected_answer)
  }

  contains(answer_raw: string, expected_answer: string): boolean {
    return expected_answer
      .split(',')
      .some((r) => (answer_raw ?? '').split(',').indexOf(r) >= 0)
  }

  notContains(answer_raw: string, expected_answer: string): boolean {
    return !this.contains(answer_raw, expected_answer)
  }

  isFilled(answer_raw: string): boolean {
    return Boolean(answer_raw?.replace(/:/g, ''))
  }

  isEmpty(answer_raw: string): boolean {
    return !this.isFilled(answer_raw)
  }

  containsAny(answer_raw: string, expected_answer: string): boolean {
    return expected_answer
      .split(',')
      .some((r) => (answer_raw ?? '').split(',').indexOf(r) >= 0)
  }

  notContainsAny(answer_raw: string, expected_answer: string): boolean {
    return !this.containsAny(answer_raw, expected_answer)
  }

  greaterThan(answer_raw: string, expected_answer: string): boolean {
    return parseFloat(answer_raw) > parseFloat(expected_answer)
  }

  lesserThan(answer_raw: string, expected_answer: string): boolean {
    return parseFloat(answer_raw) < parseFloat(expected_answer)
  }

  getDateAnswers(
    answer_raw: string,
    expected_answer: string
  ): [string, string] {
    let date_answer = answer_raw
    let date_compare_answer = expected_answer

    const regex = /^[0-9]{2}-[0-9]{4}$/

    if (
      !!date_answer &&
      regex.test(date_answer) &&
      !!date_compare_answer &&
      regex.test(date_compare_answer)
    ) {
      const [answer_month, answer_year] = answer_raw.split('-')
      date_answer = `${answer_year}-${answer_month}-01`

      const [compare_month, compare_year] = date_compare_answer.split('-')
      date_compare_answer = `${compare_year}-${compare_month}-01`
    }

    return [date_answer, date_compare_answer]
  }

  isBefore(answer_raw: string, expected_answer: string): boolean {
    const [date_answer, date_compare_answer] = this.getDateAnswers(
      answer_raw,
      expected_answer
    )

    return (
      !!date_compare_answer &&
      !!date_answer &&
      moment(date_answer).isBefore(date_compare_answer)
    )
  }

  isAfter(answer_raw: string, expected_answer: string): boolean {
    const [date_answer, date_compare_answer] = this.getDateAnswers(
      answer_raw,
      expected_answer
    )

    return (
      !!date_compare_answer &&
      !!date_answer &&
      moment(date_answer).isAfter(date_compare_answer)
    )
  }

  operationMap: { [string]: (string, string) => Function } = {
    EQUALS: (a: string, b: string) => this.equals(a, b),
    NOT_EQUALS: (a: string, b: string) => this.notEquals(a, b),
    CONTAINS: (a: string, b: string) => this.contains(a, b),
    NOT_CONTAINS: (a: string, b: string) => this.notContains(a, b),
    IS_FILLED: (a: string, b: string) => this.isFilled(a),
    IS_EMPTY: (a: string, b: string) => this.isEmpty(a),
    CONTAINS_ANY: (a: string, b: string) => this.containsAny(a, b),
    NOT_CONTAINS_ANY: (a: string, b: string) => this.notContainsAny(a, b),
    GREATER_THAN: (a: string, b: string) => this.greaterThan(a, b),
    LESS_THAN: (a: string, b: string) => this.lesserThan(a, b),
    IS_BEFORE: (a: string, b: string) => this.isBefore(a, b),
    IS_AFTER: (a: string, b: string) => this.isAfter(a, b),
  }

  resolveConditionalRule(rule: ConditionalRule): boolean {
    const { expected_answer, operator, question } = rule

    const ruleQuestion = this.questions?.find((q) => q.id === question)

    const isQuestionHiddenByConLogic =
      ruleQuestion && !this.resolveQuestion(ruleQuestion)

    if (isQuestionHiddenByConLogic) return false

    const answer = this.answers?.find((answer) => answer.question === question)

    const operation = this.operationMap[operator || '']

    if (
      !answer &&
      ['IS_EMPTY', 'NOT_EQUALS', 'NOT_CONTAINS'].includes(operator)
    )
      return true
    if (!operation || !answer) return false

    return operation(answer?.answer_raw, expected_answer || '')
  }

  resolveConditionalRuleGroup(
    conditionalRuleGroup: ConditionalRuleGroup
  ): boolean {
    const { conditional_rules } = conditionalRuleGroup

    if (!conditional_rules.length) return false

    return conditional_rules.every((rule) => this.resolveConditionalRule(rule))
  }

  resolveQuestion(question: Question): boolean {
    this.depth += 1

    if (this.depth > MAX_DEPTH) {
      return true
    }

    const conditionalRuleGroups = question.conditional_rule_groups || []

    const conditionalRuleGroupsWithRules = conditionalRuleGroups.filter(
      (group) => group.conditional_rules.length
    )

    if (!conditionalRuleGroupsWithRules.length) return true

    return conditionalRuleGroupsWithRules.some((group) =>
      this.resolveConditionalRuleGroup(group)
    )
  }
}

export default ConditionalRulesResolver
